import * as React from 'react'
import {
  type FieldArrayWithId,
  UseFieldArrayRemove,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form'

import {
  Box,
  CollapsableCard,
  SearchField,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  AvailablePayrollConceptFieldFragment,
  CompensationValidationFragment,
  CompensationsFormFieldsDocument,
  CompensationsValueObjectsResultTypeEnum,
  EmployeePayrollRunElementEditCompensationsFormFragment,
  PayrollRunEmployeesCompensationFormFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useSearchField } from 'modules/compensations/hooks/useSearchField'
import { CompensationsFormAggregates } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormAggregates'
import { CompensationAmountInputField } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/CompensationAmountInputField'
import { sortByOverridden } from 'modules/compensations/pages/CompensationsPayrollRun/CompensationsFormFields/sortingUtils'
import { SyncStateLabel } from 'modules/integrations/public'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'

const earningsConceptsCategories = [
  'earnings_benefits_in_kind',
  'earnings_fixed_salary',
  'earnings_others',
  'earnings_variable',
]

const salaryAdvanceDeductionCategory = 'deductions'

type CompensationsFormFieldsProps = {
  employeeId: number
  payrollRunId: number
  availablePayrollConcepts: Array<AvailablePayrollConceptFieldFragment>
  payrollRunEmployeeCompensations?: Array<PayrollRunEmployeesCompensationFormFragment>
}

type ReadonlyCompensationsFormFieldsProps = {
  readonly?: boolean
}

function CompensationsFormFields({
  employeeId,
  payrollRunId,
  availablePayrollConcepts,
  readonly,
  payrollRunEmployeeCompensations,
}: CompensationsFormFieldsProps & ReadonlyCompensationsFormFieldsProps) {
  const compensations: EmployeePayrollRunElementEditCompensationsFormFragment['nodes'] = useWatch({
    name: 'nodes',
  })

  const { fields, prepend, remove } =
    useFieldArray<EmployeePayrollRunElementEditCompensationsFormFragment>({ name: 'nodes' })

  const {
    filteredData: filteredAvailablePayrollConcepts,
    onSearch,
    query,
  } = useSearchField(availablePayrollConcepts, {
    searchKeys: ['label'],
  })

  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const canEditAllCompensations =
    compensations?.every((c) => c._policies?.editCompensations?.can !== false) ?? true
  const globallyReadonly = readonly || !canEditAllCompensations

  const { data: validationDataQuery, loading: isLoadingValidationData } = useQuery(
    CompensationsFormFieldsDocument,
    {
      variables: {
        employeeIds: [employeeId],
        validationType: ['discrepancy'],
        payrollRunIds: [payrollRunId],
      },
    }
  )

  const validationData = React.useMemo(
    () =>
      validationDataQuery?.compensations?.employeesCompensationsValidationsConnection?.nodes ?? [],
    [validationDataQuery]
  )

  const { conceptSums, discrepancyAlerts } = React.useMemo(() => {
    const sums: Record<
      number,
      Record<CompensationsValueObjectsResultTypeEnum, number | undefined>
    > = {}
    const alerts: Record<number, CompensationValidationFragment> = {}

    if (compensations) {
      availablePayrollConcepts.forEach((concept) => {
        // Count compensations
        const matchingCompensations = compensations.filter(
          (c) => c?.payrollConcept?.id === concept.id && c.resultType === 'compensation'
        )

        const totalAmount =
          matchingCompensations.length === 0
            ? undefined
            : matchingCompensations.reduce((sum, c) => {
                const currentSum = sum || 0
                const amount = c.isOverridden ? c?.amount : c?.compensationsAmountStrategy?.amount
                return currentSum + (amount ?? 0)
              }, 0)

        // Count results
        const matchingResults = compensations.filter(
          (c) => c?.payrollConcept?.id === concept.id && c.resultType === 'payroll_result'
        )

        const totalAmountResults =
          matchingResults.length === 0
            ? undefined
            : matchingResults.reduce((sum, c) => {
                const currentSum = sum || 0
                const amount = c.isOverridden ? c?.amount : c?.compensationsAmountStrategy?.amount
                return currentSum + (amount ?? 0)
              }, 0)

        const alert = validationData.find(
          (v) =>
            v.validationType === 'discrepancy' &&
            v.compensationsPayrollRunEmployeeCompensation?.payrollConceptId === concept.id
        )

        if (alert) {
          alerts[concept.id] = alert
        }

        sums[concept.id] = {
          compensation: totalAmount,
          payroll_result: totalAmountResults,
        }
      })
    }

    return { conceptSums: sums, discrepancyAlerts: alerts }
  }, [compensations, availablePayrollConcepts, validationData])

  const isEarningConcept = (concept: AvailablePayrollConceptFieldFragment) => {
    return earningsConceptsCategories.includes(concept?.category ?? '')
  }

  const isSalaryAdvanceDeductionConcept = (concept: AvailablePayrollConceptFieldFragment) => {
    return (
      concept?.category === salaryAdvanceDeductionCategory &&
      concept.name === DEFAULT_RESULT_CONCEPT.salary_advance
    )
  }

  const shouldShowAddButton = (concept: AvailablePayrollConceptFieldFragment) => {
    return isEarningConcept(concept) || isSalaryAdvanceDeductionConcept(concept)
  }

  return (
    <Box gap="s12">
      <Box
        flexGrow
        paddingX="s24"
        paddingBottom="s12"
        position="sticky"
        background="overlayBackground"
        style={{ zIndex: '99', top: '0px' }}
      >
        <SearchField
          variant="big"
          search={query}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      </Box>
      <Box padding="s24" paddingTop="s0">
        {filteredAvailablePayrollConcepts.length === 0 && query.trim() !== '' ? (
          <Box height="full" minHeight="s400" justifyContent="center" alignItems="center" gap="s24">
            <OneEmptyState
              title={i18n.t('compensations.payroll_run.search.no_results.title')}
              description={i18n.t('compensations.payroll_run.search.no_results.description')}
              emoji="🔍"
            />
          </Box>
        ) : (
          filteredAvailablePayrollConcepts.map((concept) => {
            const matchingCompensation = compensations.find(
              (c) => c.payrollConcept?.id === concept.id && c.resultType === 'compensation'
            )

            const employeePayrollRunElementId = matchingCompensation?.payrollConcept?.id

            const sumCompensations = conceptSums[concept.id]?.compensation
            const sumResults = conceptSums[concept.id]?.payroll_result

            const employeePayrollRunElementIds = compensations
              .filter((c) => c.payrollConcept?.id === concept.id)
              .map((c) => c.payrollConcept?.id)
            const isOpen = Boolean(employeePayrollRunElementId)

            return (
              <Box
                paddingTop="s12"
                key={concept.id}
                data-testid={`compensationsFormFields-${concept.id}`}
              >
                <CollapsableCard
                  Card={
                    <Box gap="s8">
                      <Box flexDirection="row" justifyContent="spaceBetween">
                        <Box flexDirection="row" alignItems="center" gap="s8">
                          <Text.Normal weight="bold">{concept.label} </Text.Normal>
                          {isLoading && <Spinner size="s16" />}
                        </Box>
                        {shouldShowAddButton(concept) && (
                          <F0Button
                            variant="outline"
                            icon={Add}
                            hideLabel
                            label="add"
                            type="button"
                            disabled={globallyReadonly}
                            onClick={() => {
                              // There is no way to avoid sending id and amountStrategyId since useFieldArray
                              // does not allow partial data. This is not a problem since we are not using because
                              // the mapping on the update mutation is done based on isOverridden property.
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              return prepend({
                                amountStrategyId: 0,
                                __typename: 'CompensationsEmployeesCompensation',
                                amount: null,
                                amountStrategyType: 'compensations_variable_amount_strategy',
                                compensationsAmountStrategy: {
                                  __typename: 'CompensationsVariableAmountStrategy',
                                  amount: null,
                                },
                                isOverridden: true,
                                resultType: 'compensation',
                                payrollConcept: concept,
                              })
                            }}
                          />
                        )}
                      </Box>
                      <CompensationsFormAggregates
                        unitType={concept?.unitType ?? 'money'}
                        validation={discrepancyAlerts[concept.id]}
                        inputAmount={
                          sumCompensations !== undefined ? Number(sumCompensations) : undefined
                        }
                        resultAmount={sumResults !== undefined ? Number(sumResults) : undefined}
                        isLoading={isLoading || isLoadingValidationData}
                      />
                    </Box>
                  }
                  Content={
                    <CompensationFormFields
                      readonly={globallyReadonly}
                      employeeId={employeeId}
                      payrollRunId={payrollRunId}
                      employeePayrollRunElementIds={employeePayrollRunElementIds}
                      fields={fields}
                      remove={remove}
                      onLoadSuccess={(isUpserting) => setIsLoading(isUpserting)}
                    />
                  }
                  open={isOpen}
                />
                {isOpen &&
                  payrollRunEmployeeCompensations &&
                  payrollRunEmployeeCompensations.length > 0 && (
                    <SyncStateLabel
                      payrollRunEmployeeCompensations={payrollRunEmployeeCompensations}
                      compensationConcept={concept}
                    />
                  )}
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}

function CompensationFormFields({
  employeeId,
  payrollRunId,
  employeePayrollRunElementIds,
  fields,
  ...rest
}: {
  employeeId: number
  payrollRunId: number
  employeePayrollRunElementIds: (number | undefined)[]
  fields: FieldArrayWithId<EmployeePayrollRunElementEditCompensationsFormFragment, 'nodes', 'id'>[]
  remove: UseFieldArrayRemove
  onLoadSuccess?: (isUpserting: boolean) => void
} & ReadonlyCompensationsFormFieldsProps) {
  const { control, getFieldState } =
    useFormContext<EmployeePayrollRunElementEditCompensationsFormFragment>()

  const fieldsToRender = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => employeePayrollRunElementIds.includes(field.payrollConcept?.id))
    .filter(({ field }) => field.resultType === 'compensation')
    .sort(sortByOverridden)

  return (
    <Box gap="s8">
      {fieldsToRender.map(({ field, index }) => (
        <CompensationAmountInputField<EmployeePayrollRunElementEditCompensationsFormFragment>
          key={field.id}
          employeeId={employeeId}
          payrollRunId={payrollRunId}
          amountPath={`nodes.${index}`}
          isDirty={getFieldState(`nodes.${index}`).isDirty}
          control={control}
          index={index}
          field={field}
          {...rest}
        />
      ))}
    </Box>
  )
}

export { CompensationsFormFields }
export type { CompensationsFormFieldsProps }
